import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { TileEmpty } from '../TileWrapper';
import { Clock, AlertTriangle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';

// Mock data for simulation
const MOCK_DATA = [
  { id: '1', userName: 'John Smith', avatarUrl: null, userId: '1', requirementName: 'First Aid Certificate', daysUntil: 3 },
  { id: '2', userName: 'Sarah Johnson', avatarUrl: null, userId: '2', requirementName: 'CSCS Card', daysUntil: 7 },
  { id: '3', userName: 'Mike Williams', avatarUrl: null, userId: '3', requirementName: 'Manual Handling', daysUntil: 12 },
  { id: '4', userName: 'Emma Brown', avatarUrl: null, userId: '4', requirementName: 'Fire Safety', daysUntil: 18 },
  { id: '5', userName: 'David Lee', avatarUrl: null, userId: '5', requirementName: 'Working at Height', daysUntil: 25 },
];

export function ExpiringSoonTile({ filter, size, useMockData }) {
  const navigate = useNavigate();
  const { company } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (useMockData) {
      setData(size === 'tall' ? MOCK_DATA : MOCK_DATA.slice(0, 5));
      setLoading(false);
      return;
    }

    if (!company?.id) return;

    const loadData = async () => {
      setLoading(true);
      try {
        // Get expiring qualifications (within 30 days)
        const thirtyDaysFromNow = new Date();
        thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);

        let query = supabase
          .from('qualifications')
          .select(`
            id,
            expiry_date,
            user_id,
            requirement_id,
            user_profiles!inner(id, first_name, last_name, avatar_url, company_id),
            requirements_library(id, name)
          `)
          .eq('user_profiles.company_id', company.id)
          .not('expiry_date', 'is', null)
          .gte('expiry_date', new Date().toISOString().split('T')[0])
          .lte('expiry_date', thirtyDaysFromNow.toISOString().split('T')[0])
          .order('expiry_date', { ascending: true })
          .limit(size === 'tall' ? 10 : 5);

        const { data: qualifications, error } = await query;

        if (error) throw error;

        const items = (qualifications || []).map(q => ({
          id: q.id,
          userName: `${q.user_profiles.first_name} ${q.user_profiles.last_name}`,
          avatarUrl: q.user_profiles.avatar_url,
          userId: q.user_profiles.id,
          requirementName: q.requirements_library?.name || 'Unknown',
          expiryDate: new Date(q.expiry_date),
          daysUntil: Math.ceil((new Date(q.expiry_date) - new Date()) / (1000 * 60 * 60 * 24)),
        }));

        setData(items);
      } catch (err) {
        console.error('Failed to load expiring data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [company?.id, filter?.spaceId, size, useMockData]);

  if (loading) {
    return (
      <div className="space-y-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="flex items-center gap-3 animate-pulse">
            <div className="w-8 h-8 rounded-full bg-gray-200" />
            <div className="flex-1">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-1" />
              <div className="h-3 bg-gray-200 rounded w-1/2" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (data.length === 0) {
    return <TileEmpty message="Nothing expiring soon" icon={Clock} />;
  }

  const getDaysColor = (days) => {
    if (days <= 7) return 'destructive';
    if (days <= 14) return 'warning';
    return 'secondary';
  };

  return (
    <div className="space-y-2">
      {data.map((item) => (
        <div
          key={item.id}
          onClick={() => navigate(`/profile/${item.userId}`)}
          className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
        >
          <Avatar className="h-8 w-8">
            <AvatarImage src={item.avatarUrl} />
            <AvatarFallback className="text-xs">
              {item.userName.split(' ').map(n => n[0]).join('')}
            </AvatarFallback>
          </Avatar>

          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{item.requirementName}</p>
            <p className="text-xs text-gray-500 truncate">{item.userName}</p>
          </div>

          <Badge variant={getDaysColor(item.daysUntil)} className="shrink-0">
            {item.daysUntil <= 0 ? 'Today' : `${item.daysUntil}d`}
          </Badge>
        </div>
      ))}

      {data.length >= (size === 'tall' ? 10 : 5) && (
        <button
          onClick={() => navigate('/qualifications')}
          className="w-full text-center text-sm text-blue-600 hover:text-blue-700 py-2"
        >
          View All
        </button>
      )}
    </div>
  );
}
